import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import classNames from 'classnames';
import { fabric } from 'fabric';
import 'src/fabric-overrides';
import useUserPicture from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useUserPicture';
import { pinCreate } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/pinHandlers';
import { fitPinSize } from '@pages/MediaViewer/AssetViewer/Canvas/utils/fitObject';
import { getCommentCursor } from '@pages/MediaViewer/AssetViewer/Canvas/utils/cursors';
import { updateDrawingToolCursorHint } from '@pages/MediaViewer/AssetViewer/Canvas/DrawingToolCursorHint/DrawingToolCursorHint';
import { renderPdfPageAnnotationsOnCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/utils/renderPdfPageAnnotationsOnCanvas';
import { FabricContext } from '@context/FabricContext';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useResponsive } from '@hooks/useResponsive';
import {
  PIN_DRAW_TOOL,
  isPinOrHighlightTarget
} from '@pages/MediaViewer/helpers/drawToolShape';

import './PdfPageDisplayCanvas.less';

type PdfPageDisplayCanvasProps = {
  pageNumber: number;
  pageWidth?: number;
  pageHeight?: number;
  zoomFactor: number;
  turnoverDegree: number;
  visible: boolean;
  interactive: boolean;
  isActive: boolean;
  /** Lets a comment be placed while the page is not the active one */
  onActivatePage?: (pageNumber: number) => void;
};

function setCanvasCursor(canvas: fabric.Canvas, cursor: string) {
  canvas.defaultCursor = cursor;
  canvas.hoverCursor = cursor;
  if ((canvas as any).upperCanvasEl) {
    canvas.setCursor(cursor);
  }
}

function PdfPageDisplayCanvas({
  pageNumber,
  pageWidth,
  pageHeight,
  zoomFactor,
  turnoverDegree,
  visible,
  interactive,
  isActive,
  onActivatePage
}: PdfPageDisplayCanvasProps) {
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  // The canvas is recreated when the page is resized: effects attaching to it
  // depend on this, so they attach to the new canvas too
  const [canvasVersion, setCanvasVersion] = useState(0);
  const {
    commentsList,
    selectedCommentId,
    isHideComments,
    selectComment,
    setActivePageNumber,
    setCanvasMode,
    setDocumentHighlight,
    shapesDrawingTools,
    selectedVersion,
    isMobileDrawingToolbarOpen
  } = useAssetViewerState();
  const {
    canvas: mainCanvas,
    mouseOverObjectRef,
    objectToCreateRef,
    draftPinPlacedRef,
    cursorHintRef,
    clearCanvas
  } = useContext(FabricContext);
  const { getUserPicture, setMyPicture } = useUserPicture();
  const { isMdWidth } = useResponsive();
  const assetType = selectedVersion?.metadata.type;
  const isCommentTool =
    shapesDrawingTools.shape === PIN_DRAW_TOOL &&
    (!isMdWidth || isMobileDrawingToolbarOpen);
  const shouldBlockTouchScroll = isCommentTool && isActive;

  const onSelectComment = useCallback(
    (commentId: string) => {
      selectComment(commentId);
      setActivePageNumber(pageNumber);
    },
    [pageNumber, setActivePageNumber, selectComment]
  );

  useLayoutEffect(() => {
    if (!visible || !canvasElRef.current || !pageWidth || !pageHeight) {
      return undefined;
    }

    const canvas = new fabric.Canvas(canvasElRef.current, {
      selectionColor: 'transparent',
      selectionLineWidth: 0,
      centeredScaling: true,
      targetFindTolerance: 5,
      preserveObjectStacking: true,
      perPixelTargetFind: true,
      enableRetinaScaling: false,
      imageSmoothingEnabled: false,
      width: pageWidth,
      height: pageHeight,
      selection: false,
      allowTouchScrolling: true
    });

    fabricCanvasRef.current = canvas;
    setCanvasVersion((version) => version + 1);

    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, [visible, pageWidth, pageHeight]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !canvas) return;
    canvas.allowTouchScrolling = !shouldBlockTouchScroll;
  }, [visible, shouldBlockTouchScroll, canvasVersion]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !canvas || !pageWidth || !pageHeight) return;

    canvas.setWidth(pageWidth);
    canvas.setHeight(pageHeight);

    renderPdfPageAnnotationsOnCanvas({
      canvas,
      pageNumber,
      // The selected comment is shown by the main canvas, not twice
      commentsList: selectedCommentId
        ? commentsList.filter((comment) => comment.id !== selectedCommentId)
        : commentsList,
      zoomFactor,
      turnoverDegree,
      isHideComments,
      assetType,
      overObject: mouseOverObjectRef,
      getUserPicture,
      onSelectComment,
      viewerActions: {
        selectComment,
        setCanvasMode,
        setDocumentHighlight
      }
    });
  }, [
    visible,
    pageNumber,
    pageWidth,
    pageHeight,
    commentsList,
    selectedCommentId,
    zoomFactor,
    assetType,
    turnoverDegree,
    isHideComments,
    getUserPicture,
    mouseOverObjectRef,
    onSelectComment,
    selectComment,
    setCanvasMode,
    setDocumentHighlight
  ]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!visible || !interactive || !canvas) return undefined;

    if (!isCommentTool) {
      setCanvasCursor(canvas, 'default');
      return undefined;
    }

    const pinCursor = getCommentCursor();
    setCanvasCursor(canvas, pinCursor);

    const onMouseMove = (event: fabric.IEvent<Event>) => {
      if (
        event.target &&
        isPinOrHighlightTarget(event.target as fabric.Object)
      ) {
        canvas.setCursor('pointer');
        updateDrawingToolCursorHint(cursorHintRef.current, { hidden: true });
        return;
      }
      canvas.setCursor(pinCursor);
      updateDrawingToolCursorHint(cursorHintRef.current, {
        shape: PIN_DRAW_TOOL,
        event: event.e as MouseEvent,
        hidden: draftPinPlacedRef.current
      });
    };
    const onMouseOut = () => {
      updateDrawingToolCursorHint(cursorHintRef.current, { hidden: true });
    };

    canvas.on('mouse:over', onMouseMove);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:out', onMouseOut);
    return () => {
      canvas.off('mouse:over', onMouseMove);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:out', onMouseOut);
      updateDrawingToolCursorHint(cursorHintRef.current, { hidden: true });
      setCanvasCursor(canvas, 'default');
    };
  }, [
    visible,
    interactive,
    isCommentTool,
    cursorHintRef,
    draftPinPlacedRef,
    canvasVersion
  ]);

  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (
      (!isActive && !onActivatePage) ||
      !interactive ||
      !visible ||
      !canvas ||
      !mainCanvas ||
      !isCommentTool
    ) {
      return undefined;
    }

    const onMouseDown = (e: fabric.IEvent) => {
      if (e.target || mouseOverObjectRef.current) return;
      if (!isActive) onActivatePage?.(pageNumber);

      const pointer = canvas.getPointer(e.e);

      draftPinPlacedRef.current = true;
      selectComment('new');
      setDocumentHighlight(null);
      setCanvasMode('edit');
      clearCanvas();

      mainCanvas.setWidth(canvas.getWidth());
      mainCanvas.setHeight(canvas.getHeight());
      mainCanvas.setViewportTransform([
        zoomFactor * 10,
        0,
        0,
        zoomFactor * 10,
        0,
        0
      ]);

      const { email, name } = setMyPicture();
      pinCreate({
        x: pointer.x,
        y: pointer.y,
        canvas: mainCanvas,
        overObject: mouseOverObjectRef,
        objectToCreate: objectToCreateRef,
        userEmail: email,
        userName: name,
        scale: fitPinSize(zoomFactor, assetType, mainCanvas)
      });
    };

    canvas.on('mouse:down', onMouseDown);
    return () => {
      canvas.off('mouse:down', onMouseDown);
    };
  }, [
    isActive,
    onActivatePage,
    pageNumber,
    interactive,
    visible,
    mainCanvas,
    isCommentTool,
    selectComment,
    setDocumentHighlight,
    setCanvasMode,
    setMyPicture,
    clearCanvas,
    assetType,
    zoomFactor,
    mouseOverObjectRef,
    objectToCreateRef,
    draftPinPlacedRef,
    canvasVersion
  ]);

  if (!visible || !pageWidth || !pageHeight) return null;

  return (
    <div
      className={classNames('media_viewer_pdf-page-display-canvas', {
        'b-non-interactive': !interactive
      })}
    >
      <canvas ref={canvasElRef} />
    </div>
  );
}

export default memo(PdfPageDisplayCanvas);
